import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import {
  CheckCircle2,
  AlertTriangle,
  CalendarRange,
  Link2,
  Unlink,
  Loader2,
  Mail,
  MessageSquare,
  Clock,
  Send,
  Trash2,
  HardDrive,
} from "lucide-react";
import { toast } from "sonner";

export default function Settings() {
  const [params, setParams] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [calendars, setCalendars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [showCleanupModal, setShowCleanupModal] = useState(false);
  const [cleaning, setCleaning] = useState(false);
  const [cleanupResult, setCleanupResult] = useState(null);

  const handleCleanup = async () => {
    setCleaning(true);
    try {
      const { data } = await api.post("/admin/maintenance/cleanup-test-data");
      const s = data.summary || {};
      const lines = Object.entries(s).map(([k, v]) => `${v} ${k.replace(/_/g, " ")}`);
      setCleanupResult(lines.length ? lines : ["Nothing to clean up — database is already tidy"]);
      const total = Object.values(s).reduce((a, b) => a + b, 0);
      toast.success(total ? `Test data cleanup complete (${total} rows removed)` : "Database already clean");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not run cleanup");
    } finally {
      setCleaning(false);
    }
  };

  const loadStatus = async () => {
    try {
      const { data } = await api.get("/oauth/google/status");
      setStatus(data);
      if (data.connected) {
        try {
          const cals = await api.get("/calendar/calendars");
          setCalendars(cals.data.items || []);
        } catch {
          setCalendars([]);
        }
      }
    } catch (e) {
      // 401 is handled by the global axios interceptor (logs out + redirects).
      // For any other error, fail soft — render Settings with status=null so
      // the user sees the UI instead of a blank page.
      if (e?.response?.status !== 401) {
        setStatus({ connected: false, configured: false });
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStatus();
    const flag = params.get("google");
    if (flag === "connected") {
      toast.success("Google Calendar connected");
      params.delete("google");
      setParams(params, { replace: true });
    } else if (flag === "error") {
      toast.error(`Google connection failed: ${params.get("reason") || "unknown"}`);
      params.delete("google");
      params.delete("reason");
      setParams(params, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const connect = async () => {
    setConnecting(true);
    try {
      const { data } = await api.get("/oauth/google/connect");
      window.location.href = data.authorization_url;
    } catch (e) {
      const detail = e?.response?.data?.detail || "Could not start the OAuth flow.";
      toast.error(detail);
      setConnecting(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect Google Calendar? Bookings will stop working until reconnected.")) return;
    await api.delete("/oauth/google");
    toast.success("Disconnected");
    setStatus({ connected: false, configured: status?.configured });
    setCalendars([]);
  };

  const selectCalendar = async (cal) => {
    try {
      await api.post("/calendar/select", { calendar_id: cal.id });
      toast.success(`Active calendar: ${cal.summary}`);
      loadStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not select calendar");
    }
  };

  if (loading) {
    return (
      <div className="text-white/60 text-sm py-12 flex items-center gap-2" data-testid="settings-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading settings…
      </div>
    );
  }

  return (
    <div className="space-y-8 max-w-3xl" data-testid="settings-page">
      <div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-[#FFB000] font-medium mb-2">CRM</div>
        <h1 className="text-3xl font-bold tracking-tight">Settings</h1>
        <p className="text-sm text-white/50 mt-2">Integrations and global config for the booking pipeline.</p>
      </div>

      {/* Google Calendar card */}
      <section className="bg-[#121214] border border-white/5 rounded-2xl p-6" data-testid="settings-google-card">
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 rounded-xl bg-[#FFB000]/10 border border-[#FFB000]/20 flex items-center justify-center flex-shrink-0">
            <CalendarRange className="w-5 h-5 text-[#FFB000]" strokeWidth={1.75} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-semibold">Google Calendar</h2>
              {status?.connected ? (
                <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] font-medium text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 px-2 py-0.5 rounded">
                  <CheckCircle2 className="w-3 h-3" /> Connected
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] font-medium text-white/50 bg-white/5 border border-white/10 px-2 py-0.5 rounded">
                  Not connected
                </span>
              )}
            </div>
            <p className="text-sm text-white/55 mt-1.5 leading-relaxed">
              Powers booking from the public chatbot, the Hear Ashley flow, and AI agents.
              Bookings are written to a dedicated <span className="text-[#FFB000]">Repliant Arc Demos</span> calendar.
            </p>

            {!status?.configured && (
              <div className="mt-4 text-[12px] text-amber-300/90 bg-amber-500/[0.05] border border-amber-500/20 rounded-lg p-3 flex items-start gap-2" data-testid="settings-google-not-configured">
                <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <div>
                  <div className="font-medium">Google OAuth credentials missing</div>
                  <div className="text-amber-200/70 text-[11px] mt-1 leading-relaxed">
                    Add <code className="bg-black/30 px-1 rounded">GOOGLE_CLIENT_ID</code> and <code className="bg-black/30 px-1 rounded">GOOGLE_CLIENT_SECRET</code> to <code className="bg-black/30 px-1 rounded">backend/.env</code>, restart the backend, then refresh this page.
                  </div>
                </div>
              </div>
            )}

            {status?.connected && (
              <div className="mt-4 grid grid-cols-2 gap-3 text-xs">
                <div className="bg-black/20 border border-white/5 rounded-lg p-3">
                  <div className="text-white/40 uppercase tracking-[0.12em] text-[10px]">Connected as</div>
                  <div className="text-white/85 mt-1 font-mono truncate" data-testid="settings-google-email">{status.email}</div>
                </div>
                <div className="bg-black/20 border border-white/5 rounded-lg p-3">
                  <div className="text-white/40 uppercase tracking-[0.12em] text-[10px]">Active calendar</div>
                  <div className="text-white/85 mt-1 truncate" data-testid="settings-active-calendar">{status.calendar_summary || "Auto"}</div>
                </div>
              </div>
            )}

            <div className="mt-5 flex items-center gap-3">
              {status?.connected ? (
                <button
                  data-testid="google-disconnect-btn"
                  onClick={disconnect}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-medium text-white/70 hover:text-white border border-white/10 hover:border-white/20 transition-colors"
                >
                  <Unlink className="w-3.5 h-3.5" /> Disconnect
                </button>
              ) : (
                <button
                  data-testid="google-connect-btn"
                  onClick={connect}
                  disabled={!status?.configured || connecting}
                  className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold text-black bg-[#FFB000] hover:bg-[#FFC633] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {connecting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />}
                  Connect Google
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Calendar picker */}
        {status?.connected && calendars.length > 0 && (
          <div className="mt-6 pt-6 border-t border-white/5">
            <div className="text-[10px] uppercase tracking-[0.15em] text-white/40 font-medium mb-3">Choose calendar for bookings</div>
            <div className="grid gap-2">
              {calendars
                .filter((c) => c.access_role === "owner" || c.access_role === "writer")
                .map((c) => (
                  <button
                    key={c.id}
                    data-testid={`calendar-option-${c.id}`}
                    onClick={() => selectCalendar(c)}
                    className={`flex items-center justify-between text-left px-4 py-3 rounded-lg border transition-colors ${
                      status.calendar_id === c.id
                        ? "bg-[#FFB000]/10 border-[#FFB000]/40"
                        : "bg-black/20 border-white/5 hover:border-white/15"
                    }`}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: c.background || "#FFB000" }} />
                      <span className="text-sm truncate">{c.summary}</span>
                      {c.primary && <span className="text-[10px] uppercase text-white/40 tracking-[0.12em]">primary</span>}
                    </div>
                    {status.calendar_id === c.id && <CheckCircle2 className="w-4 h-4 text-[#FFB000] flex-shrink-0" />}
                  </button>
                ))}
            </div>
          </div>
        )}
      </section>

      {/* Email integration card (read-only summary) */}
      <section className="bg-[#121214] border border-white/5 rounded-2xl p-6" data-testid="settings-email-card">
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center flex-shrink-0">
            <Mail className="w-5 h-5 text-emerald-400" strokeWidth={1.75} />
          </div>
          <div className="flex-1">
            <h2 className="text-lg font-semibold">Email</h2>
            <p className="text-sm text-white/55 mt-1.5 leading-relaxed">
              Booking confirmations to visitors and alerts to <code className="text-[#FFB000]">patrick@repliantarc.com</code>.
              Configure your email provider's API key in <code className="bg-black/30 px-1 rounded">backend/.env</code>.
            </p>
          </div>
        </div>
      </section>

      {/* SMS reminder card */}
      <section className="bg-[#121214] border border-white/5 rounded-2xl p-6" data-testid="settings-sms-card">
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 rounded-xl bg-sky-500/10 border border-sky-500/20 flex items-center justify-center flex-shrink-0">
            <MessageSquare className="w-5 h-5 text-sky-400" strokeWidth={1.75} />
          </div>
          <div className="flex-1">
            <h2 className="text-lg font-semibold">SMS Reminders</h2>
            <p className="text-sm text-white/55 mt-1.5 leading-relaxed">
              Visitors with a phone number on their booking get a 24-hour reminder SMS — proven to lift demo show-up rate ~23 points.
              Configure your SMS provider credentials in <code className="bg-black/30 px-1 rounded">backend/.env</code> to activate.
            </p>
            <div className="mt-3 flex items-center gap-2 text-[11px] text-white/40">
              <Clock className="w-3 h-3" />
              Background scheduler runs every 5 minutes · idempotent (won't double-send)
            </div>
          </div>
        </div>
      </section>

      {/* Post-demo follow-up card */}
      <section className="bg-[#121214] border border-white/5 rounded-2xl p-6" data-testid="settings-followup-card">
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 rounded-xl bg-purple-500/10 border border-purple-500/20 flex items-center justify-center flex-shrink-0">
            <Send className="w-5 h-5 text-purple-400" strokeWidth={1.75} />
          </div>
          <div className="flex-1">
            <h2 className="text-lg font-semibold">Post-demo Follow-up</h2>
            <p className="text-sm text-white/55 mt-1.5 leading-relaxed">
              Auto-sends "Thanks for jumping on with Patrick" 30 min after each demo's end time, with an optional Stripe payment link prefilled.
              Adds ~12-18% lift on close rate. Set <code className="bg-black/30 px-1 rounded">STRIPE_PROPOSAL_LINK</code> in backend/.env once your Stripe payment link is ready.
            </p>
            <div className="mt-3 flex items-center gap-2 text-[11px] text-white/40">
              <Clock className="w-3 h-3" />
              Triggers automatically · idempotent · same scheduler cycle as reminders
            </div>
          </div>
        </div>
      </section>

      {/* Danger zone — destructive admin maintenance */}
      <section
        className="bg-[#121214] border border-red-500/20 rounded-2xl p-6"
        data-testid="settings-danger-zone"
      >
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 rounded-xl bg-red-500/10 border border-red-500/30 flex items-center justify-center flex-shrink-0">
            <HardDrive className="w-5 h-5 text-red-400" strokeWidth={1.75} />
          </div>
          <div className="flex-1">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <h2 className="text-lg font-semibold text-red-300">Cleanup Test Data</h2>
              <button
                type="button"
                onClick={() => { setCleanupResult(null); setShowCleanupModal(true); }}
                data-testid="cleanup-test-data-btn"
                className="flex items-center gap-2 px-3.5 py-2 rounded-md text-xs font-medium text-red-300 bg-red-500/5 hover:bg-red-500/10 border border-red-500/20 hover:border-red-500/40 transition-colors"
              >
                <Trash2 className="w-3.5 h-3.5" strokeWidth={2} />
                Run cleanup
              </button>
            </div>
            <p className="text-sm text-white/55 mt-1.5 leading-relaxed">
              Removes every campaign, client, prospect, call, appointment, consent record, referral, and user with a name starting with <code className="bg-black/30 px-1 rounded">TEST</code> or <code className="bg-black/30 px-1 rounded">VendorAuditCo</code>, plus all archived clients. Real campaigns, clients, and appointments are preserved.
            </p>
            {cleanupResult && (
              <div className="mt-3 p-3 bg-emerald-500/5 border border-emerald-500/20 rounded-md text-xs text-emerald-200" data-testid="cleanup-result">
                <div className="font-medium mb-1">Last run:</div>
                <ul className="list-disc list-inside space-y-0.5 text-emerald-100/80">
                  {cleanupResult.map((line, i) => <li key={i}>{line}</li>)}
                </ul>
              </div>
            )}
          </div>
        </div>
      </section>

      {showCleanupModal && (
        <div
          className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          data-testid="cleanup-modal"
          onClick={() => !cleaning && setShowCleanupModal(false)}
        >
          <div
            className="bg-[#121214] border border-red-500/30 rounded-2xl p-6 max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-red-500/15 border border-red-500/30 flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-5 h-5 text-red-400" strokeWidth={2} />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-white">Clean up test data?</h2>
                <p className="text-sm text-white/55 mt-1.5 leading-relaxed">
                  This will permanently delete all test data. Real clients, campaigns, and appointments preserved.
                </p>
                <p className="text-xs text-white/40 mt-3">This cannot be undone.</p>
              </div>
            </div>
            <div className="flex items-center gap-3 mt-6">
              <button
                onClick={() => setShowCleanupModal(false)}
                disabled={cleaning}
                className="flex-1 px-4 py-2.5 rounded-md text-sm font-medium text-white/70 hover:text-white bg-white/5 hover:bg-white/10 border border-white/10 transition-colors disabled:opacity-50"
                data-testid="cleanup-cancel"
              >
                Cancel
              </button>
              <button
                onClick={async () => { await handleCleanup(); setShowCleanupModal(false); }}
                disabled={cleaning}
                className="flex-1 px-4 py-2.5 rounded-md text-sm font-semibold text-white bg-red-500/80 hover:bg-red-500 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="cleanup-confirm"
              >
                {cleaning ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                {cleaning ? "Cleaning…" : "Yes, run cleanup"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
